/**
 * Enterprise System Detail Page
 *
 * Displays comprehensive details about a single enterprise system with tabs:
 * - Overview: System summary, statistics, status
 * - Modules: List of modules with details
 * - Schema: Tables and columns browser
 * - APIs: API endpoints explorer
 * - Integration: Integration patterns and documentation
 * - Quality: Quality assessment dashboard
 * - Research: Research job history
 */

import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Edit,
  Trash2,
  RefreshCw,
  AlertCircle,
  Loader2,
  LayoutGrid,
  Layers,
  Database,
  Code,
  Network,
  Award,
  History,
  Sparkles,
  BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { getEnterpriseSystem, triggerSystemResearch } from '../lib/api/enterpriseSystemsApi';
import OverviewTab from '../components/enterprise-systems/OverviewTab';
import ModulesTab from '../components/enterprise-systems/ModulesTab';
import SchemaTab from '../components/enterprise-systems/SchemaTab';
import APIsTab from '../components/enterprise-systems/APIsTab';
import IntegrationTab from '../components/enterprise-systems/IntegrationTab';
import QualityTab from '../components/enterprise-systems/QualityTab';
import DocumentationTab from '../components/enterprise-systems/DocumentationTab';
import ResearchHistoryTab from '../components/enterprise-systems/ResearchHistoryTab';

type TabKey = 'overview' | 'modules' | 'schema' | 'apis' | 'integration' | 'documentation' | 'quality' | 'research';

interface Tab {
  key: TabKey;
  label: string;
  icon: React.FC<{ className?: string }>;
}

const TABS: Tab[] = [
  { key: 'overview', label: 'Overview', icon: LayoutGrid },
  { key: 'modules', label: 'Modules', icon: Layers },
  { key: 'schema', label: 'Schema', icon: Database },
  { key: 'apis', label: 'APIs', icon: Code },
  { key: 'integration', label: 'Integration', icon: Network },
  { key: 'documentation', label: 'Documentation', icon: BookOpen },
  { key: 'quality', label: 'Quality', icon: Award },
  { key: 'research', label: 'Research', icon: History },
];

const EnterpriseSystemDetailPage: React.FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // Fetch system details
  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['enterprise-system', systemId],
    queryFn: () => getEnterpriseSystem(systemId!),
    enabled: !!systemId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Trigger research mutation
  const triggerResearchMutation = useMutation({
    mutationFn: () => triggerSystemResearch(systemId!, false),
    onSuccess: (data) => {
      toast.success(
        `Research started! Job ID: ${data.job_id}. Switch to Research tab to view progress.`,
        { duration: 6000 }
      );
      // Invalidate research jobs query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['research-jobs', systemId] });
      // Switch to research tab
      setActiveTab('research');
    },
    onError: (error: any) => {
      toast.error(`Failed to start research: ${error.message}`);
    },
  });

  /**
   * Handle edit system
   */
  const handleEdit = () => {
    navigate(`/enterprise-systems/${systemId}/edit`);
  };

  /**
   * Handle delete system
   */
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this system?')) {
      // TODO: Implement delete
      console.log('Delete system:', systemId);
    }
  };

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    refetch();
  };

  /**
   * Handle trigger research
   */
  const handleTriggerResearch = () => {
    if (window.confirm('Start AI research to extract modules, schema, APIs, and documentation? This may take 5-15 minutes.')) {
      triggerResearchMutation.mutate();
    }
  };

  /**
   * Get quality score color
   */
  const getQualityColor = (score: number): string => {
    if (score >= 0.9) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 0.8) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 0.7) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (score >= 0.6) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  /**
   * Get quality grade
   */
  const getQualityGrade = (score: number): string => {
    if (score >= 0.9) return 'A';
    if (score >= 0.8) return 'B';
    if (score >= 0.7) return 'C';
    if (score >= 0.6) return 'D';
    return 'F';
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-neutral-600">Loading system details...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !data) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Failed to Load System
          </h3>
          <p className="text-neutral-600 text-sm mb-6">
            {error instanceof Error ? error.message : 'System not found.'}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate('/enterprise-systems')}
              className="px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors"
            >
              Back to List
            </button>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { system } = data;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header — constrained to the same max-width as the body below so the
          top and bottom sections align at the same width. */}
      <div className="max-w-screen-2xl mx-auto px-6 pt-6">
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-neutral-600 mb-4">
            <Link
              to="/enterprise-systems"
              className="hover:text-primary-600 transition-colors"
            >
              Enterprise Systems
            </Link>
            <span>/</span>
            <span className="text-neutral-900 font-medium">
              {system.system_name}
            </span>
          </div>

          {/* Title Row */}
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-4 mb-2">
                <h1 className="text-3xl font-bold text-neutral-900 truncate">
                  {system.system_name}
                </h1>

                {/* Quality Badge */}
                <div
                  className={`flex-shrink-0 px-4 py-2 rounded-lg border-2 text-lg font-bold ${getQualityColor(
                    system.quality_score
                  )}`}
                >
                  {getQualityGrade(system.quality_score)} •{' '}
                  {Math.round(system.quality_score * 100)}%
                </div>
              </div>

              <p className="text-neutral-600">
                {system.vendor} • {system.category}
                {system.version && ` • Version ${system.version}`}
              </p>

              {/* Meta Info */}
              <div className="mt-3 flex items-center gap-4 text-sm text-neutral-500">
                <span>
                  Research Version: <strong>v{system.research_version}</strong>
                </span>
                {system.last_researched && (
                  <span>
                    Last Researched:{' '}
                    <strong>
                      {new Date(system.last_researched).toLocaleDateString()}
                    </strong>
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 ml-6">
              <button
                onClick={handleRefresh}
                disabled={isFetching}
                className="p-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors disabled:opacity-50"
                aria-label="Refresh"
              >
                <RefreshCw
                  className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`}
                />
              </button>

              <button
                onClick={handleTriggerResearch}
                disabled={triggerResearchMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors disabled:opacity-50"
              >
                {triggerResearchMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    AI Research
                  </>
                )}
              </button>

              <button
                onClick={handleEdit}
                className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>

              <button
                onClick={handleDelete}
                className="p-2 border border-red-300 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                aria-label="Delete"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="mt-6 grid grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Layers className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-900">
                    {data.statistics?.modules || 0}
                  </p>
                  <p className="text-sm text-blue-700">Modules</p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Database className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-900">
                    {data.statistics?.tables || 0}
                  </p>
                  <p className="text-sm text-purple-700">Tables</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Code className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-900">
                    {data.statistics?.api_endpoints || 0}
                  </p>
                  <p className="text-sm text-green-700">APIs</p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Network className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-900">
                    {data.statistics?.documentation_links || 0}
                  </p>
                  <p className="text-sm text-orange-700">Docs</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mt-6 border-b border-neutral-200">
            <nav className="flex gap-6">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.key;

                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                      isActive
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-neutral-600 hover:text-neutral-900 hover:border-neutral-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg border border-neutral-200 p-8">
          {activeTab === 'overview' && (
            <OverviewTab system={system} statistics={data.statistics} />
          )}

          {activeTab === 'modules' && <ModulesTab systemId={systemId!} />}

          {activeTab === 'schema' && <SchemaTab systemId={systemId!} />}

          {activeTab === 'apis' && <APIsTab systemId={systemId!} />}

          {activeTab === 'integration' && <IntegrationTab systemId={systemId!} />}

          {activeTab === 'documentation' && <DocumentationTab systemId={systemId!} />}

          {activeTab === 'quality' && <QualityTab systemId={systemId!} />}

          {activeTab === 'research' && (
            <ResearchHistoryTab systemId={systemId!} systemName={system.system_name} />
          )}
        </div>
      </div>
    </div>
  );
};

export default EnterpriseSystemDetailPage;
